import { Text } from "@mdotm/shadcn/legacy/components";
import { themeCSSVars } from "@mdotm/shadcn/legacy/themes";

/**
 * The density chart's legend, drawn below the plot: one entry per series plus one for the fitted-normal
 * reference.
 *
 * Presentational only — nothing here toggles a series. Highcharts' own legend is off for this chart
 * kind (see `chartTypeDrawsLegend`), and a click target that looked like a legend but did not act like
 * one would be worse than a static key.
 *
 * **Drawn to Highcharts' legend metrics**, because it sits in the slot the stacked-column and line charts
 * fill with a real Highcharts legend and has to read as the same object: a 12px dot for an area series,
 * `Body/M/Bold` labels (Gotham 12/700, which is Highcharts' own `itemStyle`), and the spacings below. Two
 * deliberate departures, both from the ticket: one of the two series swatches is hollow, since two filled
 * dots this size out-weigh the labels they belong to; and the fitted-normal reference gets one shared
 * entry, where Highcharts would draw one per source — drawn as one dash per drawn series, in that
 * series' colour, stacked in the one symbol, which is how the curves are painted on the plot.
 *
 * The mean lines are deliberately absent: they are already labelled inline on the plot, and solid
 * versus dashed is what separates them from the reference curves.
 */

export type DensityChartLegendProps = {
	/** Names the light-blue series — the same string the chart uses. */
	mainMetricLabel: string;
	/** Names the dark-blue series. Rendered only with {@link hasProposal}. */
	secondaryMetricLabel: string;
	/** @default false */
	hasProposal?: boolean;
	/**
	 * Drops the {@link mainMetricLabel} entry, for a chart drawn under "Highlight proposal" — which hides
	 * that series outright here rather than dimming it. A key that names a series absent from the plot is
	 * worse than no key.
	 *
	 * @default false
	 */
	hideMainMetric?: boolean;
	/**
	 * Drops the fitted-normal entry, for a chart drawn with `showNormalFit={false}`.
	 *
	 * @default true
	 */
	showNormalFit?: boolean;
	/**
	 * Which series swatch is drawn as an outline rather than a fill.
	 *
	 * @default 'main'
	 */
	outlinedSeries?: "main" | "secondary";
};

/** Matches the chart's own colours: light blue for the main series, dark blue for the proposal. */
const mainSwatchColor = themeCSSVars.palette_S400;
const secondarySwatchColor = themeCSSVars.palette_B400;

/**
 * Highcharts' `legend.symbolWidth`. Every swatch is drawn in a box this wide, which is what puts the
 * labels of a dot entry and of a line entry at the same x — a dot centred in the box clears its right
 * edge by 2px, exactly as Highcharts' own 12px symbol inset does.
 */
const symbolWidth = 16;
/** Highcharts' `legend.symbolPadding`: the gap from the symbol box to the label. */
const symbolPadding = 5;
/** Highcharts' `legend.itemDistance`: the gap between two entries. */
const itemDistance = 20;
/** The diameter Highcharts draws an area series' legend symbol at. */
const dotDiameter = 12;

/**
 * Full-strength, like Highcharts' own symbol — the area's `fillOpacity` belongs to a shape the size of
 * the plot, and reproducing it in a 12px dot only makes the swatch hard to read against the card.
 */
function SeriesSwatch({ color, outlined }: { color: string; outlined: boolean }): React.JSX.Element {
	return (
		<svg width={symbolWidth} height={symbolWidth} viewBox={`0 0 ${symbolWidth} ${symbolWidth}`} aria-hidden="true">
			<circle
				cx={symbolWidth / 2}
				cy={symbolWidth / 2}
				r={dotDiameter / 2 - (outlined ? 0.75 : 0)}
				fill={outlined ? "none" : color}
				stroke={outlined ? color : "none"}
				strokeWidth={1.5}
			/>
		</svg>
	);
}

/** The gap between two stacked fitted-normal dashes, centre to centre. */
const normalFitLineSpacing = 5;

/**
 * The reference curves' dash: one full-width dashed line per drawn series, in that series' colour, in the
 * order the series entries list them. The lines are spread about the centre of Highcharts' 16px line
 * symbol, so a single one sits where Highcharts' own would. Each is drawn at the 1.5 stroke a legend line
 * takes, and the dash pattern is the plot's `4,3` scaled by that stroke, which is how Highcharts itself
 * converts a `dashStyle` — so the swatch and the curves dash at the same rhythm.
 */
function NormalFitSwatch({ colors }: { colors: Array<string> }): React.JSX.Element {
	const firstY = symbolWidth / 2 - ((colors.length - 1) * normalFitLineSpacing) / 2;
	return (
		<svg width={symbolWidth} height={symbolWidth} viewBox={`0 0 ${symbolWidth} ${symbolWidth}`} aria-hidden="true">
			{colors.map((color, index) => (
				<line
					key={color}
					x1={0}
					y1={firstY + index * normalFitLineSpacing}
					x2={symbolWidth}
					y2={firstY + index * normalFitLineSpacing}
					stroke={color}
					strokeWidth={1.5}
					strokeDasharray="6 4.5"
				/>
			))}
		</svg>
	);
}

function LegendEntry({ swatch, label }: { swatch: React.JSX.Element; label: string }): React.JSX.Element {
	return (
		<div className="flex items-center" style={{ gap: symbolPadding }}>
			{swatch}
			<Text type="Body/M/Bold">{label}</Text>
		</div>
	);
}

export function DensityChartLegend(props: DensityChartLegendProps): React.JSX.Element {
	const hasProposal = props.hasProposal ?? false;
	const hideMainMetric = props.hideMainMetric ?? false;
	const showNormalFit = props.showNormalFit ?? true;
	const outlinedSeries = props.outlinedSeries ?? "main";
	// The series the plot draws, in the order their entries are listed below.
	const drawnSeriesColors = [
		...(hideMainMetric ? [] : [mainSwatchColor]),
		...(hasProposal ? [secondarySwatchColor] : []),
	];

	return (
		<div
			className="flex flex-wrap items-center justify-center gap-y-1 px-4"
			// A Highcharts legend leaves `chart.spacingBottom` under itself, and the chart above this row
			// already leaves that much under its axis labels — so nearly all of the space this row needs is
			// below it. The 2px above is what lands the labels at the y a real legend's would sit at.
			style={{ columnGap: itemDistance, paddingTop: 2, paddingBottom: 15 }}
		>
			{!hideMainMetric && (
				<LegendEntry
					swatch={<SeriesSwatch color={mainSwatchColor} outlined={outlinedSeries === "main"} />}
					label={props.mainMetricLabel}
				/>
			)}
			{hasProposal && (
				<LegendEntry
					swatch={<SeriesSwatch color={secondarySwatchColor} outlined={outlinedSeries === "secondary"} />}
					label={props.secondaryMetricLabel}
				/>
			)}
			{showNormalFit && drawnSeriesColors.length > 0 && (
				<LegendEntry swatch={<NormalFitSwatch colors={drawnSeriesColors} />} label="Normal fit" />
			)}
		</div>
	);
}
